import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';

// SMS compliance settings (Phase 5). SMS is OFF by default and cannot send in
// production until the studio is A2P-10DLC approved AND recipients have
// consented. This page is read-mostly: admins can see registration status and
// acknowledge the compliance terms, but cannot self-grant 'approved' — that is
// flipped out-of-band by the operator once the carrier campaign clears.

export type SmsRegistrationStatus =
  | 'not_configured'
  | 'awaiting_registration'
  | 'approved'
  | 'suspended';

interface SmsConfigRow {
  sms_enabled: boolean;
  sms_registration_status: SmsRegistrationStatus;
  sms_brand_status: string | null;
  sms_campaign_status: string | null;
  sms_compliance_notes: string | null;
  sms_terms_acknowledged_at: string | null;
  sms_terms_acknowledged_by: string | null;
  allowed_categories: string[];
  can_send: boolean;
  opted_in_count: number;
  updated_at: string;
}

interface RpcError {
  message: string;
}
type SmsRpc = <T>(
  fn: 'get_sms_config' | 'acknowledge_sms_terms',
) => Promise<{ data: T | null; error: RpcError | null }>;

function smsRpc<T>(
  fn: 'get_sms_config' | 'acknowledge_sms_terms',
): Promise<{ data: T | null; error: RpcError | null }> {
  return (supabase.rpc as unknown as SmsRpc)<T>(fn);
}

export interface SmsConfig {
  smsEnabled: boolean;
  registrationStatus: SmsRegistrationStatus;
  brandStatus: string | null;
  campaignStatus: string | null;
  complianceNotes: string | null;
  termsAcknowledgedAt: string | null;
  termsAcknowledgedBy: string | null;
  allowedCategories: string[];
  canSend: boolean;
  optedInCount: number;
  updatedAt: string;
}

function mapConfig(row: SmsConfigRow): SmsConfig {
  return {
    smsEnabled: row.sms_enabled,
    registrationStatus: row.sms_registration_status,
    brandStatus: row.sms_brand_status,
    campaignStatus: row.sms_campaign_status,
    complianceNotes: row.sms_compliance_notes,
    termsAcknowledgedAt: row.sms_terms_acknowledged_at,
    termsAcknowledgedBy: row.sms_terms_acknowledged_by,
    allowedCategories: row.allowed_categories ?? [],
    canSend: row.can_send,
    optedInCount: row.opted_in_count,
    updatedAt: row.updated_at,
  };
}

export function useSmsConfig() {
  return useQuery({
    queryKey: ['sms-config'],
    queryFn: async (): Promise<SmsConfig | null> => {
      const { data, error } = await smsRpc<SmsConfigRow[]>('get_sms_config');
      if (error) throw new Error(error.message);
      const row = data?.[0];
      return row ? mapConfig(row) : null;
    },
  });
}

export function useAcknowledgeSmsTerms() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (): Promise<string> => {
      const { data, error } = await smsRpc<string>('acknowledge_sms_terms');
      if (error) throw new Error(error.message);
      return data ?? new Date().toISOString();
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['sms-config'] });
    },
  });
}

// In-app SMS consent capture (G1). Lets an admin record a contact's opt-in or
// opt-out collected in-app (e.g. an enrollment checkbox / signed form) with a
// documented `source` for the TCPA audit trail. The RPC is admin-gated and does
// NOT enable SMS or bypass the send gate.
export interface RecordSmsConsentInput {
  phone: string;
  optIn: boolean;
  source: string;
  recipientUserId?: string | null;
}

type RecordConsentRpc = (
  fn: 'admin_record_sms_consent',
  args: {
    p_phone: string;
    p_opt_in: boolean;
    p_source: string;
    p_recipient_user_id: string | null;
  },
) => Promise<{ data: string | null; error: RpcError | null }>;

export function useRecordSmsConsent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: RecordSmsConsentInput): Promise<string> => {
      const { data, error } = await (supabase.rpc as unknown as RecordConsentRpc)(
        'admin_record_sms_consent',
        {
          p_phone: input.phone.trim(),
          p_opt_in: input.optIn,
          p_source: input.source.trim(),
          p_recipient_user_id: input.recipientUserId ?? null,
        },
      );
      if (error) throw new Error(error.message);
      return data ?? (input.optIn ? 'opted_in' : 'opted_out');
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['sms-config'] });
    },
  });
}
